import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { forecastApi } from "../utils/api";
import { classifyError, shouldShowError } from "../utils/errorClassifier";
import { EndpointErrorLabel } from "./ErrorComponents";
import FieldHelp from "../help/FieldHelp";
import { getFieldHelp } from "../help/registry";

// Used when /forecast_machines is unavailable (older backend): present the
// machine listt the parent already passes in as a single group, so the tab still
// works instead of showing an empty picker.
const buildFallbackGroups = (machines = []) => {
  const usable = machines.filter(
    (m) => m && m.name && !["VIRTUAL", "ENVIRONMENT"].includes((m.category || "").toUpperCase())
  );
  if (usable.length === 0) return [];
  return [
    {
      parent_name: "All machines",
      parent_meter_id: null,
      parent_category: null,
      machines: usable.map((m) => ({
        machine_name: m.name,
        meter_id: m.meter_id || m.name,
        category: m.category || null,
      })),
    },
  ];
};

const ForecastingChart = ({ username, availableMachines = [] }) => {
  const [selectedMachine, setSelectedMachine] = useState("");
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forecastError, setForecastError] = useState(null);
  const [mapeData, setMapeData] = useState(null);
  const [mapeLoading, setMapeLoading] = useState(false);
  const [mapeError, setMapeError] = useState(null);

  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState("");

  // Forecastable machines grouped by the parent they hang under. Keyed on the
  // real hierarchy parent rather than a GRID: only 69 of 329 machines fleet-wide
  // sit under one, so a strict main->machine pairing leaves most clients with an
  // empty second dropdown.
  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    forecastApi
      .get("/forecast_machines", { params: { username } })
      .then((res) => {
        if (cancelled) return;
        const groups = res?.data?.parents || [];
        setParents(groups.length ? groups : buildFallbackGroups(availableMachines));
      })
      .catch((err) => {
        // An older backend has no /forecast_machines. Fall back to the machine
        // list the parent component already passes in, so the tab still works
        // against a deployment that predates the two-step picker.
        console.error("Machine tree fetch error:", err);
        if (!cancelled) setParents(buildFallbackGroups(availableMachines));
      });
    return () => {
      cancelled = true;
    };
  }, [username, availableMachines]);

  const machinesForParent = useMemo(() => {
    const group = parents.find((p) => p.parent_name === selectedParent);
    return group ? group.machines : [];
  }, [parents, selectedParent]);

  // Default to the first parent, then to its first machine. Re-runs whenever the
  // parent changes so the machine dropdown never points at something the parent
  // does not contain.
  useEffect(() => {
    if (parents.length === 0) return;
    if (!parents.some((p) => p.parent_name === selectedParent)) {
      setSelectedParent(parents[0].parent_name);
    }
  }, [parents, selectedParent]);

  useEffect(() => {
    if (machinesForParent.length === 0) {
      if (selectedMachine) setSelectedMachine("");
      return;
    }
    if (!machinesForParent.some((m) => m.machine_name === selectedMachine)) {
      setSelectedMachine(machinesForParent[0].machine_name);
    }
  }, [machinesForParent, selectedMachine]);

  useEffect(() => {
    if (!selectedMachine || !username) return;

    const fetchForecast = async () => {
      setLoading(true);
      setForecastError(null);

      try {
        const response = await forecastApi.get("/get_forecasted_data", {
          params: {
            username: username,
            machine_name: selectedMachine,
          },
        });

        setForecastData(response.data);
        setForecastError(null);
      } catch (err) {
        console.error("Forecast fetch error:", err);
        const classifiedErr = classifyError(err, 'get_forecasted_data');
        if (classifiedErr.severity !== 'silent') {
          setForecastError(classifiedErr);
        }
        setForecastData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchForecast();
  }, [selectedMachine, username]);

  // Fetch MAPE accuracy data
  useEffect(() => {
    if (!selectedMachine || !username) return;

    const fetchMape = async () => {
      setMapeLoading(true);
      setMapeError(null);
      try {
        const response = await forecastApi.get("/mape", {
          params: {
            username: username,
            machine_name: selectedMachine,
          },
        });
        setMapeData(response.data);
        setMapeError(null);
      } catch (err) {
        console.error("MAPE fetch error:", err);
        const classifiedErr = classifyError(err, 'mape');
        if (classifiedErr.severity !== 'silent') {
          setMapeError(classifiedErr);
        } else {
          setMapeError(null);
        }
        setMapeData(null);
      } finally {
        setMapeLoading(false);
      }
    };

    fetchMape();
  }, [selectedMachine, username]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 450,
        zoom: { enabled: true },
        toolbar: { show: true },
      },
      colors: ["#3b82f6", "#10b981"],
      stroke: {
        width: [3, 3],
        curve: "smooth",
        dashArray: [0, 6],
      },
      title: {
        text: `Energy Forecasting: ${selectedMachine}`,
        align: "left",
      },
      xaxis: {
        type: "datetime",
        labels: { datetimeUTC: false },
      },
      yaxis: {
        title: { text: "Power (kW)" },
        labels: {
          formatter: (val) => val.toFixed(2),
        },
      },
      tooltip: {
        shared: true,
        x: { format: "dd MMM yyyy HH:mm" },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
      },
      markers: {
        size: 0,
        hover: { size: 5 },
      },
      grid: {
        borderColor: "#f1f5f9",
      },
    };
  }, [selectedMachine]);

  const series = useMemo(() => {
    if (!forecastData) return [];

    const parseTimestamp = (ts) => {
      if (!ts) return Date.now();
      // Replace first space with 'T' for safer cross-browser date parsing
      // Handles format like "2026-03-24 17:00:00" or "2026-03-25 16:00:00+05:00"
      const isoTs = ts.replace(" ", "T");
      return new Date(isoTs).getTime();
    };

    // Historical data
    const historicalSeries =
      forecastData.last_24_hours?.map((item) => ({
        x: parseTimestamp(item.timestamp),
        y: item.power_kw,
      })) || [];

    let predictionSeries = [];
    const predictions = forecastData.predictions?.next_4_hours;

    if (predictions) {
      const values = [
        predictions.next_1h,
        predictions.next_2h,
        predictions.next_3h,
        predictions.next_4h,
      ];

      // The API tells us exactly which hours it predicted. Trust that over
      // deriving hours locally, so the line cannot drift out of alignment when
      // the forecast was generated at a different hour than this request.
      if (Array.isArray(predictions.timestamps) && predictions.timestamps.length) {
        predictionSeries = values
          .map((y, i) => ({ x: parseTimestamp(predictions.timestamps[i]), y }))
          .filter((p) => p.y !== null && p.y !== undefined && !Number.isNaN(p.x));
      } else {
        // Fallback: derive the hours. Preference for the base timestamp is
        // reference_timestamp_pak -> last historical point -> now.
        let baseTimestamp;
        if (forecastData.reference_timestamp_pak) {
          baseTimestamp = parseTimestamp(forecastData.reference_timestamp_pak);
        } else if (historicalSeries.length > 0) {
          baseTimestamp = historicalSeries[historicalSeries.length - 1].x;
        } else {
          baseTimestamp = Date.now();
        }

        const hour = 3600000;
        predictionSeries = values.map((y, i) => ({
          x: baseTimestamp + hour * (i + 1),
          y,
        }));
      }

      // Join the two lines ONLY when the forecast really does start the hour after
      // the last reading. When the feed lags (predictions.data_lag_hours > 0) the
      // forecast begins several hours later, and prepending the last historical
      // point made the chart draw one smooth segment across that empty span — a
      // multi-hour "prediction" no model produced, which read as confidently as the
      // real values. Leaving the gap open shows exactly the 4 forecast hours and
      // makes the lag visible instead of hiding it.
      const HOUR_MS = 3600000;
      const lastHistorical = historicalSeries[historicalSeries.length - 1];
      const startsNextHour =
        lastHistorical &&
        predictionSeries.length > 0 &&
        predictionSeries[0].x - lastHistorical.x === HOUR_MS;

      if (startsNextHour) {
        predictionSeries.unshift(lastHistorical);
      }
    }

    const finalSeries = [
      {
        name: "Historical (Last 24h)",
        data: historicalSeries,
      },
    ];

    // Only add forecast series if there's actual prediction data
    if (predictionSeries.length > 1) {
      finalSeries.push({
        name: "Forecast (Next 4h)",
        data: predictionSeries,
      });
    }

    return finalSeries;
  }, [forecastData]);

  const noPrediction =
    forecastData?.predictions?.next_4_hours === null &&
    (forecastData?.predictions?.message ||
      forecastData?.predictions?.next_4_hours === null);

  if (parents.length === 0) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          padding: "20px",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          textAlign: "center",
        }}
      >
        No forecastable machines are available for this client.
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <h2 style={{ margin: 0 }}>
            <FieldHelp id="live.forecastChart">Load Forecasting</FieldHelp>
          </h2>
          
          {/* MAPE display */}
          {mapeLoading ? (
            <span style={{ fontSize: "14px", color: "#64748b" }}>Calculating Accuracy...</span>
          ) : mapeData ? (() => {
            // Headline is MAPE over hours above the kW floor only. Present only
            // when the API supplies it; machines served from stored
            // forecast_metrics have no such column yet, so fall back.
            const aboveFloor =
              mapeData.above_floor?.mape_percent != null
                ? mapeData.above_floor
                : null;
            const operatingSeg =
              mapeData.segments?.operating?.mape_percent != null
                ? mapeData.segments.operating
                : null;
            const mapeHelp = getFieldHelp("live.forecastMape");
            const mapeDetail = aboveFloor
              ? `This figure covers ${aboveFloor.sample_count} hours above ${aboveFloor.load_floor_kw} kW (${aboveFloor.excluded_count} low-load hours left out). Over all hours: ${mapeData.mape_percent?.toFixed(1)}% (median ${mapeData.mdape_percent?.toFixed(1)}%). Average miss: ${mapeData.mae_kw?.toFixed(1)} kW.`
              : `This figure covers all hours. Median: ${mapeData.mdape_percent?.toFixed(1)}%. Average miss: ${mapeData.mae_kw?.toFixed(1)} kW.`;
            return (
            <div 
              style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "8px",
                background: mapeData.valid ? "#f0fdf4" : "#fffbeb",
                padding: "4px 12px",
                borderRadius: "20px",
                border: `1px solid ${mapeData.valid ? "#bbf7d0" : "#fef3c7"}`,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
              }}
            >
              {/* MAPE over hours above the kW floor only. A percentage error
                  divides by the actual, so hours where the site idles near zero
                  swamp the pooled mean: the smallest 20% of hours carry 65% of it.
                  Falls back to the median when the API cannot supply the floor. */}
              <span style={{ fontSize: "13px", fontWeight: 600, color: mapeData.valid ? "#166534" : "#92400e" }}>
                {/* Replaces the badge's old title: this measurement's own figures
                    are appended to the general explanation. */}
                <FieldHelp help={{ ...mapeHelp, how: `${mapeHelp?.how || ""} ${mapeDetail}` }}>
                  {aboveFloor
                    ? `MAPE (>${aboveFloor.load_floor_kw} kW): ${aboveFloor.mape_percent?.toFixed(2)}%`
                    : `MAPE: ${mapeData.mape_percent?.toFixed(2)}%`}
                </FieldHelp>
              </span>
              {!mapeData.valid && (
                <span 
                  style={{ 
                    fontSize: "10px", 
                    background: "#f59e0b", 
                    color: "white", 
                    padding: "1px 6px", 
                    borderRadius: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase"
                  }}
                >
                  <FieldHelp id="live.forecastCalibrating">Calibrating</FieldHelp>
                </span>
              )}
            </div>
            );
          })() : null}
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <label style={{ fontWeight: 600 }}>
            <FieldHelp id="live.forecastMain">Main:</FieldHelp>
          </label>

          <select
            value={selectedParent}
            onChange={(e) => setSelectedParent(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              minWidth: "190px",
              cursor: "pointer",
            }}
          >
            {parents.map((p) => (
              <option key={p.parent_name} value={p.parent_name}>
                {/* "Main 1 (6 machines)", not "Main 1 - 6", which reads as a range. */}
                {p.parent_name}
                {` (${p.machines.length} machine${p.machines.length === 1 ? "" : "s"})`}
              </option>
            ))}
          </select>

          <label style={{ fontWeight: 600 }}>
            <FieldHelp id="live.forecastMachine">Machine:</FieldHelp>
          </label>

          <select
            value={selectedMachine}
            onChange={(e) => setSelectedMachine(e.target.value)}
            disabled={machinesForParent.length === 0}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              minWidth: "200px",
              cursor: machinesForParent.length ? "pointer" : "not-allowed",
            }}
          >
            {machinesForParent.map((m) => (
              <option key={m.meter_id} value={m.machine_name}>
                {m.machine_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            height: "450px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Loading forecasting data...
        </div>
      ) : forecastError && shouldShowError(forecastError) ? (
        <EndpointErrorLabel error={forecastError} />
      ) : forecastData ? (
        <>
          {noPrediction && (
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                marginBottom: "20px",
                color: "#0369a1",
                fontSize: "14px",
                border: "1px solid #bae6fd",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <span style={{ fontSize: "18px" }}>ℹ️</span>
              {forecastData.predictions.message ||
                "Forecasting model is currently being trained. Check back soon."}
            </div>
          )}

          <ReactApexChart
            options={chartOptions}
            series={series}
            type="line"
            height={450}
          />
        </>
      ) : (
        <div
          style={{
            height: "450px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Select a machine to view forecasting
        </div>
      )}
    </div>
  );
};

export default ForecastingChart;